import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type {
  WeightDiffDetailRow,
  WeightDiffSubAccountRow,
} from '../../types/snapshot.types';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';
import { resolvePortfolioInvestableCentsFromAudit } from './resolvePortfolioInvestableCents';

export type {
  WeightDiffDetailRow,
  WeightDiffSubAccountRow,
} from '../../types/snapshot.types';

const buildFallbackSubAccountId = (accountType: string) =>
  `audit-sub-account-${accountType}`;

type WeightDiffLike = {
  currentWeight?: number | null;
  proposedWeight?: number | null;
  deltaWeight?: number | null;
  deltaCents?: number | null;
};

/** Reconstruct dollar values from audit weight diffs (delta is source of truth). */
export const resolveWeightDiffValues = (
  diff: WeightDiffLike,
  investableCents: number,
): { currentCents: number; proposedCents: number; deltaCents: number } => {
  const deltaCents = diff.deltaCents ?? 0;
  const currentWeight = diff.currentWeight ?? 0;

  let currentCents =
    investableCents > 0 ? Math.max(0, currentWeight * investableCents) : 0;
  let proposedCents: number;

  if (currentCents === 0 && deltaCents < 0) {
    currentCents = Math.abs(deltaCents);
    proposedCents = 0;
  } else if (currentCents === 0 && deltaCents > 0) {
    proposedCents = deltaCents;
  } else {
    proposedCents = Math.max(0, currentCents + deltaCents);
  }

  return {
    currentCents,
    proposedCents,
    deltaCents,
  };
};

export const buildWeightDiffRows = (
  audit: OptimizerAuditV2Record | undefined,
  tickerMap: Record<string, string> = {},
): WeightDiffDetailRow[] => {
  if (!audit) {
    return [];
  }

  const totalMarketValueCents = (audit.subAccountResults ?? []).reduce(
    (sum, result) => sum + (result.inputMarketValueCents ?? 0),
    0,
  );
  const totalInvestableCents = resolvePortfolioInvestableCentsFromAudit(audit);
  const targetWeightMap = new Map(
    (audit.targetWeights ?? []).map((entry) => [
      entry.financialProductId ?? '',
      entry.targetWeight ?? 0,
    ]),
  );

  type AggEntry = {
    currentCents: number;
    proposedCents: number;
    deltaCents: number;
    bySubAccount: WeightDiffSubAccountRow[];
  };

  const aggregated = new Map<string, AggEntry>();

  for (const subAccount of audit.subAccountResults ?? []) {
    const marketValueCents = subAccount.inputMarketValueCents ?? 0;
    const investable = marketValueCents - (subAccount.inputCashCents ?? 0);
    const accountType = formatAuditAccountType(subAccount.accountType);
    const subAccountId =
      subAccount.subAccountId ?? buildFallbackSubAccountId(accountType);

    for (const diff of subAccount.weightDiffs ?? []) {
      const financialProductId = diff.financialProductId ?? '';
      if (!financialProductId) {
        continue;
      }

      const existing = aggregated.get(financialProductId) ?? {
        currentCents: 0,
        proposedCents: 0,
        deltaCents: 0,
        bySubAccount: [],
      };
      const { currentCents, proposedCents, deltaCents } =
        resolveWeightDiffValues(diff, investable);
      const proposedWeight =
        diff.proposedWeight ??
        (diff.currentWeight ?? 0) + (diff.deltaWeight ?? 0);

      existing.currentCents += currentCents;
      existing.proposedCents += proposedCents;
      existing.deltaCents += deltaCents;
      existing.bySubAccount.push({
        subAccountId,
        accountType,
        currentCents,
        proposedCents,
        deltaCents,
        currentWeight: diff.currentWeight ?? 0,
        proposedWeight,
        subAccountMVCents: marketValueCents,
      });
      aggregated.set(financialProductId, existing);
    }
  }

  return Array.from(aggregated.entries())
    .filter(
      ([, value]) => value.currentCents !== 0 || value.proposedCents !== 0,
    )
    .map(([financialProductId, value]) => {
      const modelTargetPct = targetWeightMap.get(financialProductId) ?? 0;
      const targetCents =
        totalInvestableCents > 0 ? modelTargetPct * totalInvestableCents : 0;

      return {
        financialProductId,
        label: tickerMap[financialProductId] ?? financialProductId.slice(-8),
        currentCents: value.currentCents,
        proposedCents: value.proposedCents,
        deltaCents: value.deltaCents,
        currentPct:
          totalMarketValueCents > 0
            ? value.currentCents / totalMarketValueCents
            : 0,
        proposedPct:
          totalMarketValueCents > 0
            ? value.proposedCents / totalMarketValueCents
            : 0,
        modelTargetPct,
        targetPct:
          totalMarketValueCents > 0 ? targetCents / totalMarketValueCents : 0,
        targetCents,
        currentInvestablePct:
          totalInvestableCents > 0
            ? value.currentCents / totalInvestableCents
            : 0,
        proposedInvestablePct:
          totalInvestableCents > 0
            ? value.proposedCents / totalInvestableCents
            : 0,
        subAccountRows: value.bySubAccount.filter(
          (row) => row.currentCents !== 0 || row.proposedCents !== 0,
        ),
      };
    })
    .sort((a, b) => Math.abs(b.deltaCents) - Math.abs(a.deltaCents));
};
